import { useId, useRef, useState } from "react";

import { env } from "@/config/env";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import type {
  AdminAccessRequest,
  LiveScope,
  LiveScopeLocation,
} from "@/lib/api/admin-access-requests";
import { deskStudioUrl } from "@/pages/admin/desk-studio-url";
import { ApiErrorNotice } from "@/lib/api/portal-error-notice";
import type { PresentedApiError } from "@/lib/api/portal-error-copy";

const REVOKE_CONFIRMATION = "Revoke";

type LiveRevokeDialogProps = {
  request: AdminAccessRequest;
  submitting: boolean;
  error: PresentedApiError | null;
  onCancel: () => void;
  onConfirm: () => void;
};

function credentialsWereIssued(status?: string | null): boolean {
  return status === "pending" || status === "issued" || status === "failed";
}

function LocationRow({ location }: { location: LiveScopeLocation }) {
  const title = location.name || location.requested_name || "Location";
  const deskUrl =
    location.status === "resolved" && location.branch_id && env.deskBaseUrl
      ? deskStudioUrl(env.deskBaseUrl, location.branch_id)
      : null;

  return (
    <li className="live-approve__row" data-testid="admin-live-revoke-row">
      <div className="live-approve__row-main">
        <span className="live-approve__name">{title}</span>
        {location.namespace ? (
          <span className="live-approve__meta">{location.namespace}</span>
        ) : null}
        {location.branch_id ? (
          <span className="live-approve__meta">
            {deskUrl ? (
              <a
                href={deskUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-live-revoke-location-id"
              >
                {location.branch_id}
              </a>
            ) : (
              location.branch_id
            )}
          </span>
        ) : null}
      </div>
      {deskUrl ? (
        <a
          className="live-approve__details"
          href={deskUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-live-revoke-more-details"
        >
          More details
        </a>
      ) : null}
    </li>
  );
}

function ScopeList({ scope }: { scope: LiveScope }) {
  if (scope.access_scope === "namespaces") {
    return (
      <ul className="live-approve__groups">
        {scope.namespaces.map((namespace) => (
          <li key={namespace.namespace}>
            <p className="live-approve__namespace">{namespace.namespace}</p>
            <ul className="live-approve__list">
              {namespace.locations.map((location) => (
                <LocationRow
                  key={location.branch_id || location.name}
                  location={location}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="live-approve__list">
      {scope.locations.map((location) => (
        <LocationRow
          key={location.branch_id || location.requested_name}
          location={location}
        />
      ))}
    </ul>
  );
}

export function LiveRevokeDialog({
  request,
  submitting,
  error,
  onCancel,
  onConfirm,
}: LiveRevokeDialogProps) {
  const scope = request.live_scope;
  const [confirmation, setConfirmation] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const confirmId = useId();
  const typedRevoke =
    confirmation.trim().toLowerCase() === REVOKE_CONFIRMATION.toLowerCase();
  const canConfirm = typedRevoke && !submitting;
  const namespaceScope = scope?.access_scope === "namespaces";
  const removesKey = credentialsWereIssued(request.credentials_status);

  useDialogFocusTrap(true, modalRef, onCancel);

  if (!scope) {
    return null;
  }

  return (
    <div className="review-modal-root" data-testid="admin-live-revoke-modal">
      <button
        type="button"
        className="review-modal-backdrop"
        aria-label="Close confirmation"
        onClick={onCancel}
      />
      <div
        ref={modalRef}
        className="review-modal review-modal--live"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h3 id={titleId} className="review-modal__title">
          Revoke live access?
        </h3>
        <p className="review-modal__body">
          {namespaceScope
            ? "This removes the granted namespaces from Glofox."
            : "This removes the granted locations from Glofox."}
          {removesKey ? " The API key is removed too." : ""}
        </p>
        <p className="live-approve__heading">
          {namespaceScope ? "NAMESPACES" : "LOCATIONS"}
        </p>
        <ScopeList scope={scope} />
        <div className="review-modal__field">
          <label className="review-modal__label" htmlFor={confirmId}>
            TYPE REVOKE TO CONFIRM
          </label>
          <input
            id={confirmId}
            className="live-approve__input"
            data-testid="admin-live-revoke-confirmation"
            value={confirmation}
            autoComplete="off"
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
          />
        </div>
        {error ? (
          <ApiErrorNotice error={error} className="review-modal__error" />
        ) : null}
        <div className="review-modal__actions">
          <button
            type="button"
            className="review-modal__btn review-modal__btn--secondary"
            data-testid="admin-review-modal-cancel"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="review-modal__btn review-modal__btn--deny"
            data-testid="admin-live-revoke-confirm"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {submitting ? "Revoking…" : "Revoke access"}
          </button>
        </div>
      </div>
    </div>
  );
}
